import { Fragment, Suspense, lazy } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { RequireAuth, useAuth } from "./context/AuthContext.jsx";
import RequireBusinessPlan from "./components/RequireBusinessPlan.jsx";
import { RequireAdminAuth } from "./context/AdminAuthContext.jsx";
import { useLocaleState } from "./i18n/locale.js";
import { localeSignature } from "./i18n/accountLocale.js";

const SaasRouteTree = lazy(() =>
  import("./pages/marketing/saas/SaasRouteTree.jsx").then((module) => ({
    default: module.SaasRouteTree,
  })),
);
const CalendarApp = lazy(() => import("./pages/calendar/CalendarApp.jsx"));
const SignInPage = lazy(() => import("./pages/auth/SignInPage.jsx"));
const SharedCalendarInvitePage = lazy(() => import("./pages/shared/SharedCalendarInvitePage.jsx"));
const SharedCalendarPage = lazy(() => import("./pages/shared/SharedCalendarPage.jsx"));
const BookingPage = lazy(() => import("./pages/booking/BookingPage.jsx"));
const BookingPolicyPage = lazy(() => import("./pages/booking/BookingPolicyPage.jsx"));
const BookingPublicLayout = lazy(() => import("./pages/booking/BookingPublicLayout.jsx"));
const BookingPreviewFramePage = lazy(() =>
  import("./components/booking/redesign/components/BookingPreviewFramePage.tsx").then((module) => ({
    default: module.BookingPreviewFramePage,
  })),
);
const PublicAppointmentFormPage = lazy(() => import("./pages/forms/PublicAppointmentFormPage.jsx"));
const PublicAddressLinkPage = lazy(() => import("./pages/address/PublicAddressLinkPage.jsx"));
const FeedbackPage = lazy(() => import("./pages/feedback/FeedbackPage.jsx"));
const ReferralRedirectPage = lazy(() => import("./pages/referrals/ReferralRedirectPage.jsx"));
const ReferralTermsPage = lazy(() => import("./pages/referrals/ReferralTermsPage.jsx"));
const CustomerPage = lazy(() => import("./pages/customer/CustomerPage.jsx"));
const StandaloneTasksPage = lazy(() => import("./pages/tasks/StandaloneTasksPage.jsx"));
const SupportHome = lazy(() => import("./pages/support/SupportHome.jsx"));
const SupportCategory = lazy(() => import("./pages/support/SupportCategory.jsx"));
const SupportArticle = lazy(() => import("./pages/support/SupportArticle.jsx"));
const SupportSearch = lazy(() => import("./pages/support/SupportSearch.jsx"));
const SupportNotFound = lazy(() => import("./pages/support/SupportNotFound.jsx"));
const AdminLoginPage = lazy(() => import("./pages/admin/AdminLoginPage.jsx"));
const AdminApp = lazy(() => import("./pages/admin/AdminApp.jsx"));
const UpgradeProPage = lazy(() => import("./pages/billing/UpgradeProPage.jsx"));
const StripeReturnToApp = lazy(() => import("./pages/stripe/StripeReturnToApp.jsx"));
const TeamJoinPage = lazy(() => import("./pages/team/TeamJoinPage.jsx"));
const TeamJoinResumePage = lazy(() =>
  import("./pages/team/TeamJoinPage.jsx").then((module) => ({
    default: module.TeamJoinResumePage,
  })),
);

function RouteLoadingFallback() {
  return <div className="authGate">Loading…</div>;
}

function withSuspense(element) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>;
}

// The June 2026 Team Scheduling invite links (/app/team-scheduling/invites/<token>)
// open the Team plan's join page.
function LegacyTeamInviteRedirect() {
  const { token = "" } = useParams();
  return <Navigate to={"/team/join/" + encodeURIComponent(token)} replace />;
}

function AccountBoundary({ children }) {
  const { user } = useAuth();
  // Money, dates and distances are formatted while rendering: when the
  // account's region changes (Region & language saved) the app starts again in it.
  const localeKey = localeSignature(useLocaleState());
  return (
    <Fragment key={(user?.id == null ? "no-account" : String(user.id)) + ":" + localeKey}>
      {children}
    </Fragment>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/stripe/return" element={withSuspense(<StripeReturnToApp />)} />
      <Route path="/stripe/refresh" element={withSuspense(<StripeReturnToApp />)} />
      <Route path="/signin" element={withSuspense(<SignInPage />)} />
      <Route path="/signup" element={withSuspense(<SignInPage />)} />
      <Route
        path="/tasks"
        element={<RequireBusinessPlan><AccountBoundary>{withSuspense(<StandaloneTasksPage />)}</AccountBoundary></RequireBusinessPlan>}
      />
      <Route path="/shared-calendar/invite/:token" element={withSuspense(<SharedCalendarInvitePage />)} />
      <Route
        path="/shared-calendar/:slug"
        element={
          <RequireAuth>
            <RequireBusinessPlan><AccountBoundary>{withSuspense(<SharedCalendarPage />)}</AccountBoundary></RequireBusinessPlan>
          </RequireAuth>
        }
      />
      <Route element={withSuspense(<BookingPublicLayout />)}>
        <Route path="/booking-preview-frame" element={withSuspense(<BookingPreviewFramePage />)} />
        <Route path="/book/:slug/policy" element={withSuspense(<BookingPolicyPage />)} />
        <Route path="/book/:slug/manage/:token" element={withSuspense(<BookingPage />)} />
        <Route path="/book/:slug/checkout" element={withSuspense(<BookingPage />)} />
        <Route path="/book/:slug" element={withSuspense(<BookingPage />)} />
      </Route>
      <Route path="/book" element={<Navigate to="/" replace />} />
      <Route path="/forms/:token" element={withSuspense(<PublicAppointmentFormPage />)} />
      <Route path="/address/:token" element={withSuspense(<PublicAddressLinkPage />)} />
      <Route path="/feedback/:token" element={withSuspense(<FeedbackPage />)} />
      <Route path="/r/:code" element={withSuspense(<ReferralRedirectPage />)} />
      <Route path="/referral-terms" element={withSuspense(<ReferralTermsPage />)} />
      <Route path="/c/:code" element={withSuspense(<CustomerPage />)} />
      <Route path="/support" element={withSuspense(<SupportHome />)} />
      <Route path="/support/category/:slug" element={withSuspense(<SupportCategory />)} />
      <Route path="/support/article/:slug" element={withSuspense(<SupportArticle />)} />
      <Route path="/support/search" element={withSuspense(<SupportSearch />)} />
      <Route path="/support/404" element={withSuspense(<SupportNotFound />)} />
      <Route path="/admin/login" element={withSuspense(<AdminLoginPage />)} />
      <Route
        path="/admin/*"
        element={
          <RequireAdminAuth>
            {withSuspense(<AdminApp />)}
          </RequireAdminAuth>
        }
      />
      {/* Team plan invites (docs/team/CONTRACT.md section 10): public, the page itself handles signing in. */}
      <Route path="/team/join/:token" element={withSuspense(<TeamJoinPage />)} />
      <Route path="/team/join" element={withSuspense(<TeamJoinResumePage />)} />
      <Route path="/app/team-scheduling/invites/:token" element={<LegacyTeamInviteRedirect />} />
      <Route path="/app/team-scheduling/*" element={<Navigate to="/app?section=team" replace />} />
      <Route
        path="/app"
        element={
          <RequireAuth>
            <RequireBusinessPlan><AccountBoundary>{withSuspense(<CalendarApp />)}</AccountBoundary></RequireBusinessPlan>
          </RequireAuth>
        }
      />
      <Route
        path="/upgrade"
        element={
          <RequireAuth>
            <AccountBoundary>{withSuspense(<UpgradeProPage />)}</AccountBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="*"
        element={<Suspense fallback={null}><SaasRouteTree /></Suspense>}
      />
    </Routes>
  );
}
